"use client";

import { useHierarchiesControllerGetAncestors, useTeamsControllerFindOne } from "@dc/gateway-jsonapi";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMe } from "@/hooks/useMe";
import { useOrganization } from "@/hooks/useOrganization";
import { useTeamsByOrganization } from "@/hooks/useTeamsByOrganization";
import type { TeamsContextType, TeamsProviderProps } from "./TeamsProvider.interface";
export const TeamsContext = createContext<TeamsContextType | null>(null);
export function TeamsProvider({
  children,
  teamId: teamIdProp
}: TeamsProviderProps) {
  const {
    currentOrganizationId,
    setCurrentOrganization
  } = useOrganization();
  const {
    data: teamsResponse,
    isLoading: teamsLoading,
    isPending: teamsPending,
    error
  } = useTeamsByOrganization();
  const {
    currentTeamId: meTeamId,
    setCurrentTeam,
    isLoading: meLoading,
    isUpdating
  } = useMe();

  // Client-side override for instant team switches via pushState.
  // Takes priority until teamIdProp catches up (real navigation).
  const [clientTeamId, setClientTeamIdState] = useState<string | null>(null);
  const prevTeamIdPropRef = useRef(teamIdProp);
  useEffect(() => {
    if (teamIdProp !== prevTeamIdPropRef.current) {
      prevTeamIdPropRef.current = teamIdProp;
      setClientTeamIdState(null);
    }
  }, [teamIdProp]);
  const setClientTeamId = useCallback((teamId: string) => {
    setClientTeamIdState(teamId);
  }, []);
  const effectiveTeamId = clientTeamId ?? teamIdProp ?? meTeamId;
  const teams = useMemo(() => teamsResponse?.data ?? [], [teamsResponse?.data]);

  // When not route-controlled and no org is selected yet (e.g. procore-plugin entry),
  // useTeamsByOrganization is disabled until currentOrganizationId resolves. Bootstrap
  // it by fetching the user's preferred team directly to derive its org from ancestors.
  const needsOrgBootstrap = !teamIdProp && !currentOrganizationId && !!meTeamId;
  const {
    data: bootstrapTeamResponse
  } = useTeamsControllerFindOne(needsOrgBootstrap ? meTeamId ?? "" : "", {
    include: "scope"
  }, {
    query: {
      enabled: needsOrgBootstrap
    }
  });
  const bootstrapTeam = bootstrapTeamResponse?.data;

  // Write-through to backend prefs when route-controlled (for bare-visit fallback)
  const hasWrittenThroughRef = useRef(false);
  useEffect(() => {
    if (teamIdProp && teamIdProp !== meTeamId && !hasWrittenThroughRef.current) {
      hasWrittenThroughRef.current = true;
      void setCurrentTeam(teamIdProp);
    }
  }, [teamIdProp, meTeamId, setCurrentTeam]);
  const prevTeamIdRef = useRef(teamIdProp);
  if (prevTeamIdRef.current !== teamIdProp) {
    prevTeamIdRef.current = teamIdProp;
    hasWrittenThroughRef.current = false;
  }
  const currentTeam = useMemo(() => {
    if (!effectiveTeamId) return null;
    return teams.find(team => team.id === effectiveTeamId) || null;
  }, [teams, effectiveTeamId]);
  const currentScopeId = useMemo(() => {
    const team_0 = currentTeam ?? bootstrapTeam;
    if (!team_0) return null;
    const scopeData = team_0.relationships?.scope?.data;
    return (Array.isArray(scopeData) ? scopeData[0]?.id : scopeData?.id) ?? null;
  }, [currentTeam, bootstrapTeam]);
  const {
    data: ancestorsData,
    isLoading: isLoadingAncestors,
    isError: isAncestorsError
  } = useHierarchiesControllerGetAncestors(currentScopeId || "", {
    scopeType: "ORGANIZATION"
  }, {
    query: {
      enabled: !!currentScopeId
    }
  });
  const organizationScopeId = useMemo(() => {
    if (!ancestorsData?.data) {
      return null;
    }
    const organizationScope = ancestorsData.data.find(ancestor => ancestor.attributes?.type === "ORGANIZATION");
    return organizationScope?.id ?? null;
  }, [ancestorsData]);
  const organizationIdFromTeam = useMemo(() => {
    if (!ancestorsData?.data) {
      return null;
    }
    const organizationScope_0 = ancestorsData.data.find(ancestor_0 => ancestor_0.attributes?.type === "ORGANIZATION");
    const orgRelationship = organizationScope_0?.relationships?.organization;
    if (orgRelationship && "data" in orgRelationship) {
      const orgData = orgRelationship.data;
      if (orgData && !Array.isArray(orgData) && "id" in orgData) {
        return orgData.id;
      }
    }
    return null;
  }, [ancestorsData]);

  // Sync organization from team's ancestors on initial load only (when NOT route-controlled)
  const hasInitializedOrgRef = useRef(false);
  useEffect(() => {
    if (teamIdProp) return; // Route-controlled: org comes from URL, no sync needed
    if (organizationIdFromTeam && !hasInitializedOrgRef.current) {
      hasInitializedOrgRef.current = true;
      if (!currentOrganizationId) {
        setCurrentOrganization(organizationIdFromTeam);
      }
    }
  }, [organizationIdFromTeam, currentOrganizationId, setCurrentOrganization, teamIdProp]);
  const hasTeamsData = teams.length > 0;
  const isInitialLoad = (teamsLoading || teamsPending) && !hasTeamsData;
  const isLoading = isInitialLoad || meLoading;
  const isSwitchingTeam = clientTeamId !== null || isUpdating;

  // False once the ancestors request has settled, so a caller can tell an org scope that
  // is still coming from one that will never arrive. A failed request counts as settled:
  // it resolved to "unavailable".
  const isOrganizationScopeLoading = !organizationScopeId && !isAncestorsError && (isLoading || !currentScopeId || isLoadingAncestors);
  const isRouteControlled = !!teamIdProp;
  const isTeamAccessDenied = isRouteControlled && !isLoading && !!currentOrganizationId && !currentTeam;
  const value: TeamsContextType = {
    currentTeam,
    currentTeamId: effectiveTeamId,
    currentScopeId: currentScopeId || null,
    organizationScopeId,
    isOrganizationScopeLoading,
    teams,
    isLoading,
    error: error ? error instanceof Error ? error : new Error(String(error)) : null,
    setCurrentTeam,
    setClientTeamId,
    isUpdating,
    isSwitchingTeam,
    isTeamAccessDenied
  };
  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>;
}